import { useCallback, useEffect, useRef, useState } from "react"

/**
 * Fetch con cancelación, sin librería de data-fetching.
 *
 * Un hook de 60 líneas cubre los endpoints que tiene este tablero, con un usuario
 * concurrente y sin necesidades offline; @tanstack/react-query sería una dependencia
 * grande para eso.
 *
 * Dos detalles que sí importan:
 *   · MANTIENE EL `data` ANTERIOR mientras refetchea (isRefetching), así cambiar un
 *     filtro no produce un flash de skeleton ni un salto de layout. La tabla vieja se
 *     atenúa y se reemplaza cuando llega la nueva.
 *   · Contador de generación contra respuestas fuera de orden: si dos requests salen
 *     seguidos y el primero vuelve último, se descarta.
 */
export interface UseApiResult<T> {
  data: T | null
  error: Error | null
  loading: boolean
  isRefetching: boolean
  refetch: () => void
}

export function useApi<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  deps: unknown[]
): UseApiResult<T> {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [loading, setLoading] = useState(true)
  const [isRefetching, setIsRefetching] = useState(false)
  const [tick, setTick] = useState(0)

  const generation = useRef(0)
  const hasData = useRef(false)

  const refetch = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    const gen = ++generation.current
    const controller = new AbortController()

    if (hasData.current) setIsRefetching(true)
    else setLoading(true)

    fetcher(controller.signal)
      .then((result) => {
        if (gen !== generation.current) return // respuesta vieja: descartar
        setData(result)
        setError(null)
        hasData.current = true
      })
      .catch((err: unknown) => {
        // Un abort no es un error del usuario: se traga para que un request cancelado
        // nunca renderice una pantalla de error.
        if (err instanceof DOMException && err.name === "AbortError") return
        if (gen !== generation.current) return
        setError(err instanceof Error ? err : new Error(String(err)))
      })
      .finally(() => {
        if (gen !== generation.current) return
        setLoading(false)
        setIsRefetching(false)
      })

    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick])

  return { data, error, loading, isRefetching, refetch }
}
